import React, { useState, useEffect } from 'react';
import Icon from '../../../../components/AppIcon';
import ToggleSwitch from '../../../va-profile-settings/components/ToggleSwitch';
import { adminProfileService } from '../../../../utils/adminProfileService';

const AdminSecurityTab = ({ profileData, onInputChange }) => {
  const [securitySettings, setSecuritySettings] = useState(profileData.security_settings || {});
  const [securityQuestions, setSecurityQuestions] = useState(profileData.security_questions || []);
  const [showQuestionForm, setShowQuestionForm] = useState(false);

  // Sync security questions when profileData changes
  useEffect(() => {
    if (profileData.security_questions) {
      setSecurityQuestions(profileData.security_questions);
    }
  }, [profileData.security_questions]);

  const PREDEFINED_QUESTIONS = [
    'What is your mother\'s maiden name?',
    'What was the name of your first pet?',
    'What city were you born in?',
    'What is your favorite book?',
    'What was the make of your first car?',
    'What is the name of the street you grew up on?',
    'What is your favorite movie?',
    'What was your first job?',
    'What is your favorite color?',
    'What is the name of your best friend from childhood?'
  ];

  // Handle security setting change
  const handleSecuritySettingChange = (setting, value) => {
    const newSettings = {
      ...securitySettings,
      [setting]: value
    };
    setSecuritySettings(newSettings);
    onInputChange('security_settings', newSettings);
  };

  // Handle security question updates
  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...securityQuestions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value
    };
    setSecurityQuestions(updatedQuestions);
    onInputChange('security_questions', updatedQuestions);
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: securityQuestions.length + 1,
      question: '',
      answer: ''
    };
    const updatedQuestions = [...securityQuestions, newQuestion];
    setSecurityQuestions(updatedQuestions);
    onInputChange('security_questions', updatedQuestions);
  };

  const handleRemoveQuestion = (index) => {
    const updatedQuestions = securityQuestions.filter((_, i) => i !== index);
    setSecurityQuestions(updatedQuestions);
    onInputChange('security_questions', updatedQuestions);
  };

  return (
    <div className="admin-security-tab">
      <style>{`
        .admin-security-tab {
          width: 100%;
        }

        .security-section {
          margin-bottom: 30px;
        }

        .security-section-title {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 15px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .security-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          margin-bottom: 12px;
        }

        .security-item-label {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .security-item-label .label {
          font-size: 14px;
          font-weight: 500;
          color: #1f2937;
        }

        .security-item-label .description {
          font-size: 12px;
          color: #6b7280;
        }

        .info-box {
          background-color: #f0f9ff;
          border: 1px solid #bfdbfe;
          border-radius: 6px;
          padding: 12px 14px;
          font-size: 13px;
          color: #0c4a6e;
          margin-bottom: 20px;
        }

        .password-expiry {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 15px;
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          margin-bottom: 12px;
        }

        .password-expiry input {
          width: 80px;
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 14px;
        }

        .question-form {
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 20px;
          margin-bottom: 15px;
        }

        .question-group {
          margin-bottom: 20px;
          padding-bottom: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .question-group:last-child {
          border-bottom: none;
        }

        .question-field {
          margin-bottom: 15px;
        }

        .question-field label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 6px;
        }

        .question-field select,
        .question-field input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
        }

        .question-field select:focus,
        .question-field input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .question-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        .btn {
          padding: 8px 14px;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary {
          background-color: #e5e7eb;
          color: #1f2937;
        }

        .btn-secondary:hover {
          background-color: #d1d5db;
        }

        .btn-danger {
          background-color: #fee2e2;
          color: #991b1b;
        }

        .btn-danger:hover {
          background-color: #fecaca;
        }

        .btn-add {
          background-color: #dbeafe;
          color: #0c4a6e;
        }

        .btn-add:hover {
          background-color: #bfdbfe;
        }
      `}</style>

      {/* Two-Factor Authentication */}
      <div className="security-section">
        <div className="security-section-title">
          <Icon name="Lock" size={18} />
          Authentication & Access Control
        </div>

        <div className="info-box">
          Configure security settings for your admin account to protect against unauthorized access.
        </div>


      </div>

      {/* Security Questions */}
      <div className="security-section">
        <div className="security-section-title">
          <Icon name="Help" size={18} />
          Security Questions
        </div>

        <div className="info-box">
          Set up security questions to help verify your identity if you forget your password. Choose answers only you would know.
        </div>

        {securityQuestions.length > 0 && !showQuestionForm && (
          <div style={{
            padding: '15px',
            backgroundColor: '#d1fae5',
            border: '1px solid #6ee7b7',
            borderRadius: '6px',
            marginBottom: '15px'
          }}>
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#065f46', marginBottom: '8px' }}>
              ✓ Security Questions Set
            </div>
            <div style={{ fontSize: '13px', color: '#047857', marginBottom: '12px' }}>
              You have {securityQuestions.length} security question{securityQuestions.length !== 1 ? 's' : ''} configured. They will be used to verify your identity during password recovery.
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => setShowQuestionForm(true)}
              style={{ fontSize: '12px' }}
            >
              Edit Questions
            </button>
          </div>
        )}

        {(showQuestionForm || securityQuestions.length === 0) && (
          <div className="question-form">
            {securityQuestions.map((q, index) => (
              <div key={index} className="question-group">
                <div className="question-field">
                  <label>Question {index + 1}</label>
                  <select
                    value={q.question || ''}
                    onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                  >
                    <option value="">Select a security question</option>
                    {PREDEFINED_QUESTIONS.map((question, idx) => (
                      <option key={idx} value={question}>
                        {question}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="question-field">
                  <label>Your Answer</label>
                  <input
                    type="text"
                    value={q.answer || ''}
                    onChange={(e) => handleQuestionChange(index, 'answer', e.target.value)}
                    placeholder="Enter your answer (case-insensitive)"
                  />
                </div>

                <div className="question-actions">
                  <button
                    className="btn btn-danger"
                    onClick={() => handleRemoveQuestion(index)}
                  >
                    Remove Question
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {(showQuestionForm || securityQuestions.length === 0) && securityQuestions.length < 3 && (
          <button
            className="btn btn-add"
            onClick={handleAddQuestion}
          >
            + Add Security Question
          </button>
        )}

        {showQuestionForm && securityQuestions.length > 0 && (
          <button
            className="btn btn-secondary"
            onClick={() => setShowQuestionForm(false)}
            style={{ marginTop: '12px' }}
          >
            Done Editing
          </button>
        )}
      </div>

      {/* Session Management */}
      <div className="security-section">
        <div className="security-section-title">
          <Icon name="Eye" size={18} />
          Session Management
        </div>

        <div className="info-box">
          Monitor and manage active login sessions for your account. You can sign out from other devices if needed.
        </div>

        <div className="security-item">
          <div className="security-item-label">
            <span className="label">Require Verification for Profile Changes</span>
            <span className="description">Verify your identity when making important account changes</span>
          </div>
          <ToggleSwitch
            checked={securitySettings.require_verification_for_profile_changes || false}
            onChange={(value) => handleSecuritySettingChange('require_verification_for_profile_changes', value)}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminSecurityTab;
